import { ref, defineComponent, provide, reactive, onMounted, onUnmounted, nextTick, watch, } from 'vue';
import Spin from '../../spin';
import useConfigInject from '../../_util/hooks/useConfigInject';
import { RadarChartContextKey } from './constants';
import { getColors } from '../../_util/chartsColors';
import * as echarts from 'echarts/core';
import { TitleComponent } from 'echarts/components';
import { RadarChart as EchartRadarChart } from 'echarts/charts';
import { CanvasRenderer, SVGRenderer } from 'echarts/renderers';
// 注册必须的组件
echarts.use([EchartRadarChart, TitleComponent, CanvasRenderer, SVGRenderer]);
export const RadarChartProps = () => ({
    quotationColor: {
        type: Number,
        default: 0,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    // 饼图颜色
    colors: {
        type: Array,
        default: () => [],
    },
    // 画布宽度
    width: {
        type: [Number, String],
        default: '420px',
    },
    // 画布高度
    height: {
        type: [Number, String],
        default: '300px',
    },
    // 图表数据[]，结构为name = String, data = []
    chartData: {
        type: Array,
        default: () => [],
    },
    // 标题
    title: {
        type: String,
        default: '',
    },
    showLegend: {
        type: Boolean,
        default: true,
    },
    legend: {
        type: Array,
        default: () => [],
    },
    legendItemGap: {
        type: Number,
        default: 12,
    },
    radius: {
        type: Number,
        default: 100,
    },
    // 相应canvas位置
    center: {
        type: Array,
        default: () => ['50%', '50%'],
    },
    // 显示在label的单位
    unit: {
        type: String,
        default: '',
    },
    // abel的数据[]，可携带name, max, 显示值等
    labelList: {
        type: Array,
        default: () => [],
    },
    // 动画时长
    animationDuration: {
        type: Number,
        default: 500,
    },
    // 是否显示分隔区域
    splitArea: {
        type: Boolean,
        default: false,
    },
    // 分隔区域个数
    splitNumber: {
        type: Number,
        default: 4,
    },
    symbol: {
        type: String,
        default: 'circle',
    },
    symbolSize: {
        type: Number,
        default: 4,
    },
    lineWidth: {
        type: Number,
        default: 1,
    },
    areaStyle: {
        type: Boolean,
        default: false,
    },
    isWrap: {
        type: Boolean,
        default: true,
    },
    axisNameFormatter: {
        type: Function,
        default: () => {
            return null;
        },
    },
    axisNameRich: {
        type: Object,
        default: () => {
            return null;
        },
    },
    showAxisLabel: {
        type: Boolean,
        default: false,
    },
    renderer: {
        type: String,
        default: 'svg',
    },
});
const RadarChart = defineComponent({
    name: 'WdRadarChart',
    props: RadarChartProps(),
    setup(props, { expose }) {
        const { prefixCls, configProvider } = useConfigInject('radar-chart', props);
        const wrapRef = ref();
        const mainRef = ref();
        let myChart = null;
        const colorsList = ref([]);
        const init = async () => {
            if (myChart) {
                myChart.clear();
                myChart.dispose();
                myChart = null;
            }
            if (!mainRef.value)
                return;
            myChart = echarts.init(mainRef.value, null, { renderer: props.renderer });
            const colors = getColors(configProvider?.mode, props.quotationColor);
            colorsList.value = props?.colors?.length ? props.colors : colors.RDDARCOLOR;
            const radarOption = await setRadar();
            const seriesOption = await setSeries();
            const option = {
                color: colorsList.value,
                title: {
                    text: props.title,
                    textStyle: {
                        fontSize: 14,
                        lineHeight: 18,
                        fontWeight: 600,
                        color: colors.TEXT,
                    },
                },
                grid: {
                    width: props.width,
                    height: props.height,
                    top: 50,
                    bottom: 2,
                },
                legend: {
                    show: props.showLegend,
                    data: props.legend,
                    bottom: -4,
                    selectedMode: false,
                    icon: 'rect',
                    itemHeight: 3,
                    itemWidth: 10,
                    itemGap: props.legendItemGap,
                    textStyle: {
                        fontSize: 12,
                        lineHeight: 18,
                        fontWeight: 'normal',
                        color: colors.TEXT,
                        padding: [0, 0, 0, 4],
                    },
                    itemStyle: {
                        borderWidth: 0,
                    },
                },
                radar: radarOption,
                series: seriesOption,
            };
            myChart.setOption(option);
        };
        // series处理
        const setSeries = () => {
            let series = [];
            if (props.chartData) {
                series = props.chartData.map((t, i) => {
                    const colors = hex2Rgba(colorsList.value[i], 0.2);
                    return {
                        name: t.name,
                        value: t.data,
                        symbol: props.symbol,
                        symbolSize: props.symbolSize,
                        lineStyle: {
                            width: props.lineWidth,
                        },
                        emphasis: {
                            areaStyle: {
                                color: new echarts.graphic.RadialGradient(0.1, 0.6, 1, [
                                    {
                                        color: colors,
                                        offset: 0,
                                    },
                                    {
                                        color: colors,
                                        offset: 1,
                                    },
                                ]),
                            },
                        },
                        areaStyle: props.areaStyle
                            ? {
                                color: new echarts.graphic.RadialGradient(0.1, 0.6, 1, [
                                    {
                                        color: colors,
                                        offset: 0,
                                    },
                                    {
                                        color: colors,
                                        offset: 1,
                                    },
                                ]),
                            }
                            : null,
                    };
                });
            }
            return [
                {
                    type: 'radar',
                    data: series,
                },
            ];
        };
        // rgb转换成rgba bgColor为hex, alpha为透明度
        const hex2Rgba = (bgColor, alpha = 1) => {
            const color = bgColor.slice(1); // 去掉'#'号
            const rgba = [
                parseInt('0x' + color.slice(0, 2)),
                parseInt('0x' + color.slice(2, 4)),
                parseInt('0x' + color.slice(4, 6)),
                alpha,
            ];
            return 'rgba(' + rgba.toString() + ')';
        };
        // 雷达图 label显示处理
        const setRadar = () => {
            if (!props.labelList.length)
                return;
            const colors = getColors(configProvider?.mode, props.quotationColor);
            // 值拼接样式
            const labelList = props.labelList.map(t => ({
                ...t,
                color: colors.TEXT,
            }));
            let tickIndex = -1;
            const option = {
                indicator: labelList,
                center: props.center,
                radius: props.radius,
                splitArea: {
                    show: props.splitArea,
                },
                axisLine: {
                    lineStyle: {
                        color: colors.RADARSPLIT_COLOR,
                    },
                },
                splitLine: {
                    lineStyle: {
                        color: colors.RADARSPLIT_COLOR,
                    },
                },
                splitNumber: props.splitNumber,
                animationDuration: props.animationDuration,
                axisName: {
                    formatter: (params, indicator) => {
                        if (props.axisNameFormatter())
                            return props.axisNameFormatter(params, indicator);
                        let text = `{params|${params}}`;
                        // label 支持传两个值，带单位
                        const val1 = [undefined].includes(indicator.val1) ? '' : indicator.val1 + props.unit;
                        if (val1) {
                            text += `${props.isWrap ? (indicator.isWrap ? '\n' : ' ') : ''}{val1|${indicator.val1 + props.unit}}`;
                        }
                        const val2 = [undefined].includes(indicator.val2) ? '' : indicator.val2 + props.unit;
                        if (val2) {
                            text += ` / {val2|${indicator.val2 + props.unit}}`;
                        }
                        return text;
                    },
                    rich: props.axisNameRich || {
                        params: {
                            color: colors.TEXT_COLOR_2,
                            lineHeight: 18,
                        },
                        val1: {
                            color: colorsList.value[0],
                            lineHeight: 20,
                            fontWeight: 600,
                        },
                        val2: {
                            color: colorsList.value[1],
                            lineHeight: 20,
                            fontWeight: 600,
                        },
                    },
                },
                axisLabel: {
                    show: props.showAxisLabel,
                    rich: {
                        a: {
                            color: colors.TEXT_COLOR_2,
                            fontSize: 12,
                            lineHeight: 12,
                            height: 1,
                            padding: [14, 0, 0, 0],
                        },
                        b: {
                            color: colors.TEXT_COLOR_2,
                            fontSize: 12,
                            lineHeight: 12,
                            height: 1,
                            padding: [4, 0, 0, 0],
                        },
                    },
                    formatter: (value) => {
                        tickIndex++;
                        if (tickIndex > 4)
                            return '';
                        return tickIndex > 0 ? `{a|${value}}` : `{b|${value}}`;
                    },
                },
            };
            // 返回值
            return option;
        };
        // 更新主题色
        const updateColor = () => {
            if (!myChart)
                return;
            const colors = getColors(configProvider?.mode, props.quotationColor);
            let tickIndex = -1;
            myChart.setOption({
                title: {
                    textStyle: {
                        color: colors.TEXT,
                    },
                },
                legend: {
                    textStyle: {
                        color: colors.TEXT,
                    },
                },
                radar: {
                    axisLine: {
                        lineStyle: {
                            color: colors.RADARSPLIT_COLOR,
                        },
                    },
                    splitLine: {
                        lineStyle: {
                            color: colors.RADARSPLIT_COLOR,
                        },
                    },
                    axisName: {
                        rich: props.axisNameRich || {
                            params: {
                                color: colors.TEXT_COLOR_2,
                            },
                            val1: {
                                color: colorsList.value[0],
                            },
                            val2: {
                                color: colorsList.value[1],
                            },
                        },
                    },
                    axisLabel: {
                        show: props.showAxisLabel,
                        rich: {
                            a: {
                                color: colors.TEXT_COLOR_2,
                            },
                            b: {
                                color: colors.TEXT_COLOR_2,
                            },
                        },
                        formatter: (value) => {
                            tickIndex++;
                            if (tickIndex > 4)
                                return '';
                            return tickIndex > 0 ? `{a|${value}}` : `{b|${value}}`;
                        },
                    },
                },
            });
        };
        watch(() => [configProvider?.mode, props.quotationColor], () => {
            nextTick(() => updateColor());
        });
        provide(RadarChartContextKey, reactive({
            RadarChartElement: mainRef,
            wrapElement: wrapRef,
        }));
        onMounted(() => {
            init();
        });
        onUnmounted(() => {
            if (myChart) {
                myChart.clear();
                myChart.dispose();
                myChart = null;
            }
            wrapRef.value = null;
            mainRef.value = null;
        });
        expose({
            /** @description init chart */
            init,
            /** @description update color */
            updateColor,
        });
        return () => {
            const bodyStyle = {
                width: props.width,
                height: props.height,
            };
            return (<Spin spinning={props.loading}>
          <div ref={mainRef} class={prefixCls.value} style={bodyStyle}></div>
        </Spin>);
        };
    },
});
export default RadarChart;
